import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Tab,
  TabPanel,
  Tabs,
  TabsBody,
  TabsHeader,
  Typography,
} from "@material-tailwind/react";
import Label from "components/Label/Label";
import React, { useState } from "react";
import ButtonSecondary from "shared/Button/ButtonSecondary";

interface ConnectedAccountDetailsProps {
  open: boolean;
  handleClose?: () => void;
  data: {
    id?: string;
    business_profile?: { name?: string; url?: string; support_email?: string };
    business_type?: string;
    status?: string;
    created?: number;
    email?: string;
    individual?: {
      email?: string;
      first_name?: string;
      last_name?: string;
      phone?: string;
      address?: { city?: string; country?: string; line1?: string; postal_code?: string };
    };
    company?: {
      name?: string;
      phone?: string;
      address?: { city?: string; country?: string; line1?: string; postal_code?: string };
    };
    external_accounts?: {
      data?: Array<{
        id?: string;
        bank_name?: string;
        last4?: string;
        currency?: string;
        country?: string;
        account_holder_name?: string;
      }>;
      total_count?: number;
    };
    metadata?: { owner_id?: string };
  } | null;
}

const ConnectedAccountDetails: React.FC<ConnectedAccountDetailsProps> = ({ open, handleClose, data }) => {
  const [activeTab, setActiveTab] = useState("basic");

  const formatAddress = (address?: { city?: string; country?: string; line1?: string; postal_code?: string }) => {
    if (!address) return "N/A";
    const { city, country, line1, postal_code } = address;
    return [line1, city, postal_code, country].filter(Boolean).join(", ") || "N/A";
  };

  const fieldIcons = {
    id: "las la-id-card",
    business_name: "las la-briefcase",
    business_type: "las la-building",
    status: "las la-info-circle",
    created: "las la-calendar-plus",
    email: "las la-envelope",
    individual_email: "las la-user",
    phone: "las la-phone",
    address: "las la-map-marker",
    external_accounts: "las la-university",
    owner_id: "las la-user-tie",
  };

  const tabs = [
    { label: "Informations de base", value: "basic" },
    { label: "Détails", value: "details" },
  ];

  return (
    <Dialog
      open={open}
      handler={() => console.log}
      className="w-full max-w-2xl mx-auto transition-all duration-300 bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
    >
      <DialogHeader className="flex items-center justify-center p-6 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
        <Typography variant="h5" className="font-bold tracking-tight text-center text-white animate-fade-in">
          Détails du Compte Connecté
        </Typography>
      </DialogHeader>
      <DialogBody className="p-6 max-h-[70vh] overflow-y-auto">
        <Tabs value={activeTab} onChange={(value: any) => setActiveTab(value as string)} className="w-full">
          <TabsHeader className="p-1 bg-gray-100 rounded-lg dark:bg-gray-800">
            {tabs.map(({ label, value }) => (
              <Tab key={value} value={value} className="p-2 text-sm font-semibold">
                {label}
              </Tab>
            ))}
          </TabsHeader>
          <TabsBody className="mt-4">
            {/* Basic Information Tab */}
            <TabPanel value="basic" className="space-y-4">
              {[
                { label: "ID du compte", value: data?.id, icon: fieldIcons.id },
                {
                  label: "Nom de l'entreprise",
                  value: data?.business_profile?.name,
                  icon: fieldIcons.business_name,
                },
                {
                  label: "Type de compte",
                  value: data?.business_type === "company" ? "Entreprise" : "Individuel",
                  icon: fieldIcons.business_type,
                },
                {
                  label: "Statut",
                  value: data?.status,
                  icon: fieldIcons.status,
                },
                {
                  label: "Créé le",
                  value: data?.created ? new Date(data.created * 1000).toLocaleDateString("fr-FR") : "N/A",
                  icon: fieldIcons.created,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex items-center space-x-2">
                    <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                    <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                  </div>
                  <Typography className="font-medium text-gray-900 dark:text-white">
                    {item.value || "N/A"}
                  </Typography>
                </div>
              ))}
            </TabPanel>

            {/* Details Tab */}
            <TabPanel value="details" className="space-y-4">
              {[
                {
                  label: "Email de support",
                  value: data?.business_profile?.support_email || data?.email || data?.individual?.email,
                  icon: fieldIcons.email,
                },
                {
                  label: "Téléphone",
                  value: data?.company?.phone || data?.individual?.phone,
                  icon: fieldIcons.phone,
                },
                {
                  label: "Adresse",
                  value: formatAddress(data?.company?.address || data?.individual?.address),
                  icon: fieldIcons.address,
                },
                {
                  label: "Comptes bancaires",
                  value:
                    data?.external_accounts &&
                    data?.external_accounts?.data &&
                    data?.external_accounts?.data?.length > 0 &&
                    data.external_accounts.data
                      .map(
                        (account) =>
                          `${account.bank_name || "N/A"} (...${account.last4 || "N/A"}) - ${account.currency?.toUpperCase() || "N/A"}`
                      )
                      .join("; "),

                  icon: fieldIcons.external_accounts,
                },
                {
                  label: "Propriétaire ID",
                  value: data?.metadata?.owner_id,
                  icon: fieldIcons.owner_id,
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-3 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 hover:shadow-md"
                >
                  <div className="flex flex-col space-y-2">
                    <div className="flex items-center space-x-2">
                      <i className={`${item.icon} text-xl text-blue-500 dark:text-blue-400`}></i>
                      <Label className="font-semibold text-gray-700 dark:text-gray-200">{item.label} :</Label>
                    </div>
                    <Typography className="font-medium text-gray-900 dark:text-white">
                      {item.value || "N/A"}
                    </Typography>
                  </div>
                </div>
              ))}
            </TabPanel>
          </TabsBody>
        </Tabs>
      </DialogBody>
      <DialogFooter className="flex justify-center p-4 space-x-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
        <ButtonSecondary
          onClick={handleClose}
          className="px-6 py-2 text-gray-900 transition-colors duration-200 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          Quitter
        </ButtonSecondary>
      </DialogFooter>
    </Dialog>
  );
};

export default ConnectedAccountDetails;
